import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    SystemToolbar,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    PlusIcon,
    FileIcon,
    UserIcon
} from "../../anatomy/icons";

const NAME = "SystemToolbar";

const USAGE_CODE = `<SystemToolbar
    actions={[
        {
            onPress: () => createFile(),
            shortcut: "mod+N",
            title: "New"
        },
        {
            onPress: () => openFile(),
            shortcut: "mod+O",
            title: "Open"
        },
        {
            onPress: () => saveFile(),
            shortcut: "mod+S",
            title: "Save"
        }
    ]}
    title="File"
/>`;

const VARIANTS = [
    {
        label: "variant=\"ghost\"",
        variant: "ghost",
        type: "neutral"
    },
    {
        label: "variant=\"outline\"",
        variant: "outline",
        type: "neutral"
    },
    {
        label: "variant=\"tonal\"",
        variant: "tonal",
        type: "neutral"
    },
    {
        label: "variant=\"filled\" · type=\"primary\"",
        variant: "filled",
        type: "primary"
    }
] as const;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const STACK_STYLE: CSSProperties = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex",
    gap: 16
};

const ITEM_STYLE: CSSProperties = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex",
    gap: 12
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("systemToolbarPage.itemsTitle"),
        id: "items"
    },
    {
        title: t("systemToolbarPage.submenusTitle"),
        id: "submenus"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const SystemToolbarPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        lastAction,
        setLastAction
    ] = useState("");

    const handleAction = (title: string) => () => setLastAction(title);

    const lastActionText = lastAction ? t("systemToolbarPage.lastAction", [
        lastAction
    ]) : t("systemToolbarPage.noAction");

    const menuActions = [
        {
            title: t("systemToolbarPage.new"),
            onPress: () => undefined,
            icon: PlusIcon
        },
        {
            title: t("systemToolbarPage.open"),
            onPress: () => undefined,
            icon: FileIcon
        }
    ];

    const renderItem = (label: string, node: ReactNode) => <div
        style={ITEM_STYLE}
        key={label}
    >
        {node}
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("systemToolbarPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("systemToolbarPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("systemToolbarPage.overviewCaption")}
            >
                <div
                    style={STACK_STYLE}
                >
                    <SystemToolbar
                        actions={[
                            {
                                onPress: handleAction(t("systemToolbarPage.new")),
                                title: t("systemToolbarPage.new"),
                                shortcut: "mod+N"
                            },
                            {
                                onPress: handleAction(t("systemToolbarPage.open")),
                                title: t("systemToolbarPage.open"),
                                shortcut: "mod+O"
                            },
                            {
                                onPress: handleAction(t("systemToolbarPage.save")),
                                title: t("systemToolbarPage.save"),
                                shortcut: "mod+S"
                            }
                        ]}
                        title={t("systemToolbarPage.file")}
                    />
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {lastActionText}
                    </Text>
                </div>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("systemToolbarPage.itemsTitle")}
            id="items"
        >
            <Paragraph>
                {t("systemToolbarPage.itemsText")}
            </Paragraph>
            <Preview>
                <SystemToolbar
                    actions={[
                        {
                            title: t("systemToolbarPage.duplicate"),
                            onPress: () => undefined,
                            shortcut: "mod+D",
                            icon: FileIcon
                        },
                        {
                            title: t("systemToolbarPage.rename"),
                            onPress: () => undefined,
                            shortcut: "F2"
                        },
                        {
                            disabledReason: t("systemToolbarPage.archiveReason"),
                            title: t("systemToolbarPage.archive"),
                            isDisabled: true
                        },
                        {
                            title: t("systemToolbarPage.delete"),
                            onPress: () => undefined,
                            isSeparatorBefore: true,
                            shortcut: "Delete",
                            type: "danger"
                        }
                    ]}
                    title={t("systemToolbarPage.edit")}
                    variant="outline"
                />
            </Preview>
        </Section>
        <Section
            title={t("systemToolbarPage.submenusTitle")}
            id="submenus"
        >
            <Paragraph>
                {t("systemToolbarPage.submenusText")}
            </Paragraph>
            <Preview>
                <SystemToolbar
                    actions={[
                        {
                            title: t("systemToolbarPage.copyLink"),
                            onPress: () => undefined
                        },
                        {
                            content: [
                                {
                                    title: t("systemToolbarPage.pdf"),
                                    onPress: () => undefined
                                },
                                {
                                    content: [
                                        {
                                            title: t("systemToolbarPage.png"),
                                            onPress: () => undefined
                                        },
                                        {
                                            title: t("systemToolbarPage.jpg"),
                                            onPress: () => undefined
                                        }
                                    ],
                                    title: t("systemToolbarPage.image")
                                }
                            ],
                            title: t("systemToolbarPage.export"),
                            isSeparatorBefore: true
                        }
                    ]}
                    title={t("systemToolbarPage.share")}
                    variant="outline"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("systemToolbarPage.variantsText")}
            </Paragraph>
            <Preview>
                {
                    VARIANTS.map((item) => renderItem(item.label, <SystemToolbar
                        title={t("systemToolbarPage.file")}
                        variant={item.variant}
                        actions={menuActions}
                        type={item.type}
                    />))
                }
                {
                    renderItem("icon", <SystemToolbar
                        title={t("sample.item1")}
                        actions={menuActions}
                        variant="outline"
                        icon={UserIcon}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("systemToolbarPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => renderItem(`size="${size}"`, <SystemToolbar
                        title={t("systemToolbarPage.file")}
                        actions={menuActions}
                        variant="outline"
                        size={size}
                    />))
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("systemToolbarPage.statesText")}
            </Paragraph>
            <Preview>
                {
                    renderItem("isDisabled", <SystemToolbar
                        title={t("systemToolbarPage.file")}
                        actions={menuActions}
                        variant="outline"
                        isDisabled={true}
                    />)
                }
                {
                    renderItem("actions={[]}", <SystemToolbar
                        title={t("systemToolbarPage.file")}
                        onPress={() => undefined}
                        variant="outline"
                        actions={[]}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("systemToolbarPage.a11yRoles"),
                    t("systemToolbarPage.a11yTitle"),
                    t("systemToolbarPage.a11yKeyboard"),
                    t("systemToolbarPage.a11ySub"),
                    t("systemToolbarPage.a11yShortcut"),
                    t("systemToolbarPage.a11yDisabled"),
                    t("systemToolbarPage.a11yTouch"),
                    t("systemToolbarPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("systemToolbarPage.propActions"),
                        type: "Array<ISystemToolbarAction>",
                        name: "actions"
                    },
                    {
                        description: t("systemToolbarPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("systemToolbarPage.propTitleKey"),
                        type: "keyof Translation",
                        name: "titleKey"
                    },
                    {
                        description: t("systemToolbarPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        type: "\"filled\" | \"tonal\" | \"outline\" | \"ghost\" | \"link\"",
                        description: t("systemToolbarPage.propVariant"),
                        defaultValue: "\"ghost\"",
                        name: "variant"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("systemToolbarPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        type: "\"xSmall\" | \"small\" | \"medium\" | \"large\"",
                        description: t("systemToolbarPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("systemToolbarPage.propIsDisabled"),
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("systemToolbarPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("systemToolbarPage.propOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        description: t("systemToolbarPage.propStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "style"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("systemToolbarPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("systemToolbarPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph>
                {t("systemToolbarPage.actionsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("systemToolbarPage.actionTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("systemToolbarPage.actionTitleKey"),
                        type: "keyof Translation",
                        name: "titleKey"
                    },
                    {
                        description: t("systemToolbarPage.actionTitleProps"),
                        type: "StyleProp<TextStyle>",
                        name: "titleProps"
                    },
                    {
                        description: t("systemToolbarPage.actionIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("systemToolbarPage.actionOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("systemToolbarPage.actionShortcut"),
                        type: "string | Array<KbdKey>",
                        name: "shortcut"
                    },
                    {
                        description: t("systemToolbarPage.actionType"),
                        type: "\"neutral\" | \"danger\"",
                        name: "type"
                    },
                    {
                        description: t("systemToolbarPage.actionIsDisabled"),
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("systemToolbarPage.actionDisabledReason"),
                        name: "disabledReason",
                        type: "string"
                    },
                    {
                        description: t("systemToolbarPage.actionIsSeparatorBefore"),
                        name: "isSeparatorBefore",
                        type: "boolean"
                    },
                    {
                        description: t("systemToolbarPage.actionContent"),
                        type: "Array<ISystemToolbarAction>",
                        name: "content"
                    },
                    {
                        description: t("systemToolbarPage.actionStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "style"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default SystemToolbarPage;
